import type { RootState } from '@src/store/store';
import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import type {
  ReportStatus,
  IPlaceReport,
  IAppUserReport,
  ITrainingReport,
  IReportListResponse,
} from 'src/types/report';

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import api, { endpoints } from 'src/lib/axios';

// ----------------------------------------------------------------------

interface ReportState<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string;
}

interface SingleReportState<T> {
  data: T | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string;
}

export interface ReportsState {
  trainings: ReportState<ITrainingReport>;
  trainingDetail: SingleReportState<ITrainingReport>;
  places: ReportState<IPlaceReport>;
  users: ReportState<IAppUserReport>;
  userDetail: SingleReportState<IAppUserReport>;
}

export const initialState: ReportsState = {
  trainings: {
    data: [],
    total: 0,
    page: 1,
    limit: 20,
    status: 'idle',
    error: '',
  },
  trainingDetail: {
    data: null,
    status: 'idle',
    error: '',
  },
  places: {
    data: [],
    total: 0,
    page: 1,
    limit: 20,
    status: 'idle',
    error: '',
  },
  users: {
    data: [],
    total: 0,
    page: 1,
    limit: 20,
    status: 'idle',
    error: '',
  },
  userDetail: {
    data: null,
    status: 'idle',
    error: '',
  },
};

// ----------------------------------------------------------------------
// Training Reports Thunks
// ----------------------------------------------------------------------

interface FetchReportsParams {
  status?: ReportStatus | 'all';
  page?: number;
  limit?: number;
}

export const loadTrainingReports = createAsyncThunk<
  IReportListResponse<ITrainingReport>,
  FetchReportsParams | void
>('reports/loadTrainingReports', async (paramsInput) => {
  const params = paramsInput || {};
  const queryParams = new URLSearchParams();
  if (params.status && params.status !== 'all') queryParams.append('status', params.status);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());

  const url = `${endpoints.admin.reports.trainings}${queryParams.toString() ? `?${queryParams}` : ''}`;
  const response = await api.get<IReportListResponse<ITrainingReport>>(url);
  return response.data;
});

export const updateTrainingReportStatus = createAsyncThunk<
  ITrainingReport,
  { id: string; status: ReportStatus }
>('reports/updateTrainingReportStatus', async ({ id, status }) => {
  const response = await api.patch<ITrainingReport>(
    `${endpoints.admin.reports.trainings}/${id}/status`,
    { status }
  );
  return response.data;
});

/**
 * KAN-631 — decide what happens to the reported publication.
 *
 * Distinct from `updateTrainingReportStatus`, which only records how the queue
 * feels about a report. This one acts on the world: DELETE destroys the
 * publication and its media and emails its author. It is not undoable, which is
 * why the view asks for confirmation before dispatching it.
 */
export const moderateTrainingReport = createAsyncThunk<
  ITrainingReport,
  { id: string; action: 'KEEP' | 'DELETE' }
>('reports/moderateTrainingReport', async ({ id, action }) => {
  const response = await api.post<ITrainingReport>(
    `${endpoints.admin.reports.trainings}/${id}/moderate`,
    { action }
  );
  return response.data;
});

export const loadTrainingReportDetail = createAsyncThunk<ITrainingReport, string>(
  'reports/loadTrainingReportDetail',
  async (id) => {
    const response = await api.get<ITrainingReport>(`${endpoints.admin.reports.trainings}/${id}`);
    return response.data;
  }
);

// ----------------------------------------------------------------------
// Place Reports Thunks
// ----------------------------------------------------------------------

export const loadPlaceReports = createAsyncThunk<
  IReportListResponse<IPlaceReport>,
  FetchReportsParams | void
>('reports/loadPlaceReports', async (paramsInput) => {
  const params = paramsInput || {};
  const queryParams = new URLSearchParams();
  if (params.status && params.status !== 'all') queryParams.append('status', params.status);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());

  const url = `${endpoints.admin.reports.places}${queryParams.toString() ? `?${queryParams}` : ''}`;
  const response = await api.get<IReportListResponse<IPlaceReport>>(url);
  return response.data;
});

export const updatePlaceReportStatus = createAsyncThunk<
  IPlaceReport,
  { id: string; status: ReportStatus }
>('reports/updatePlaceReportStatus', async ({ id, status }) => {
  const response = await api.patch<IPlaceReport>(`${endpoints.admin.reports.places}/${id}/status`, {
    status,
  });
  return response.data;
});

// ----------------------------------------------------------------------
// User Reports Thunks
// ----------------------------------------------------------------------

export const loadUserReports = createAsyncThunk<
  IReportListResponse<IAppUserReport>,
  FetchReportsParams | void
>('reports/loadUserReports', async (paramsInput) => {
  const params = paramsInput || {};
  const queryParams = new URLSearchParams();
  if (params.status && params.status !== 'all') queryParams.append('status', params.status);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());

  const url = `${endpoints.admin.reports.users}${queryParams.toString() ? `?${queryParams}` : ''}`;
  const response = await api.get<IReportListResponse<IAppUserReport>>(url);
  return response.data;
});

export const updateUserReportStatus = createAsyncThunk<
  IAppUserReport,
  { id: string; status: ReportStatus }
>('reports/updateUserReportStatus', async ({ id, status }) => {
  const response = await api.patch<IAppUserReport>(
    `${endpoints.admin.reports.users}/${id}/status`,
    { status }
  );
  return response.data;
});

export const loadUserReportDetail = createAsyncThunk<IAppUserReport, string>(
  'reports/loadUserReportDetail',
  async (id) => {
    const response = await api.get<IAppUserReport>(`${endpoints.admin.reports.users}/${id}`);
    return response.data;
  }
);

// ----------------------------------------------------------------------
// Slice
// ----------------------------------------------------------------------

const slice = createSlice({
  name: 'reports',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Training Reports
    builder
      .addCase(loadTrainingReports.pending, (state: Draft<ReportsState>) => {
        state.trainings.status = 'loading';
      })
      .addCase(
        loadTrainingReports.fulfilled,
        (
          state: Draft<ReportsState>,
          action: PayloadAction<IReportListResponse<ITrainingReport>>
        ) => {
          state.trainings.status = 'succeeded';
          state.trainings.data = action.payload.data;
          state.trainings.total = action.payload.total;
          state.trainings.page = action.payload.page;
          state.trainings.limit = action.payload.limit;
          state.trainings.error = '';
        }
      )
      .addCase(loadTrainingReports.rejected, (state: Draft<ReportsState>, action) => {
        state.trainings.status = 'failed';
        state.trainings.error = action.error.message || 'Failed to load training reports';
      })
      .addCase(updateTrainingReportStatus.fulfilled, (state: Draft<ReportsState>, action) => {
        const index = state.trainings.data.findIndex((report) => report.id === action.payload.id);
        if (index !== -1) {
          state.trainings.data[index] = action.payload;
        }
      })
      .addCase(moderateTrainingReport.fulfilled, (state: Draft<ReportsState>, action) => {
        const index = state.trainings.data.findIndex((report) => report.id === action.payload.id);

        if (action.meta.arg.action === 'DELETE') {
          /*
            The report row is GONE, not updated.

            `PublicationReport.publicationId` is `onDelete: Cascade` in the
            schema, so deleting the publication deletes every report filed
            against it. Leaving the row in the list would show a moderator a
            queue entry that 404s the moment they click it. Verified against the
            live API: after a delete, GET on the report answers 404.
          */
          if (index !== -1) {
            state.trainings.data.splice(index, 1);
            state.trainings.total = Math.max(0, state.trainings.total - 1);
          }
          if (state.trainingDetail.data?.id === action.payload.id) {
            state.trainingDetail.data = action.payload;
          }
          return;
        }

        if (index !== -1) {
          state.trainings.data[index] = action.payload;
        }
        // The detail pane is what the moderator is looking at, so it has to
        // reflect the decision without a refetch.
        if (state.trainingDetail.data?.id === action.payload.id) {
          state.trainingDetail.data = action.payload;
        }
      })
      // Training Report Detail
      .addCase(loadTrainingReportDetail.pending, (state: Draft<ReportsState>) => {
        state.trainingDetail.status = 'loading';
        state.trainingDetail.error = '';
      })
      .addCase(
        loadTrainingReportDetail.fulfilled,
        (state: Draft<ReportsState>, action: PayloadAction<ITrainingReport>) => {
          state.trainingDetail.status = 'succeeded';
          state.trainingDetail.data = action.payload;
          state.trainingDetail.error = '';
        }
      )
      .addCase(loadTrainingReportDetail.rejected, (state: Draft<ReportsState>, action) => {
        state.trainingDetail.status = 'failed';
        state.trainingDetail.error = action.error.message || 'Failed to load training report';
      });

    // Place Reports
    builder
      .addCase(loadPlaceReports.pending, (state: Draft<ReportsState>) => {
        state.places.status = 'loading';
      })
      .addCase(
        loadPlaceReports.fulfilled,
        (state: Draft<ReportsState>, action: PayloadAction<IReportListResponse<IPlaceReport>>) => {
          state.places.status = 'succeeded';
          state.places.data = action.payload.data;
          state.places.total = action.payload.total;
          state.places.page = action.payload.page;
          state.places.limit = action.payload.limit;
          state.places.error = '';
        }
      )
      .addCase(loadPlaceReports.rejected, (state: Draft<ReportsState>, action) => {
        state.places.status = 'failed';
        state.places.error = action.error.message || 'Failed to load place reports';
      })
      .addCase(updatePlaceReportStatus.fulfilled, (state: Draft<ReportsState>, action) => {
        const index = state.places.data.findIndex((report) => report.id === action.payload.id);
        if (index !== -1) {
          state.places.data[index] = action.payload;
        }
      });

    // User Reports
    builder
      .addCase(loadUserReports.pending, (state: Draft<ReportsState>) => {
        state.users.status = 'loading';
      })
      .addCase(
        loadUserReports.fulfilled,
        (
          state: Draft<ReportsState>,
          action: PayloadAction<IReportListResponse<IAppUserReport>>
        ) => {
          state.users.status = 'succeeded';
          state.users.data = action.payload.data;
          state.users.total = action.payload.total;
          state.users.page = action.payload.page;
          state.users.limit = action.payload.limit;
          state.users.error = '';
        }
      )
      .addCase(loadUserReports.rejected, (state: Draft<ReportsState>, action) => {
        state.users.status = 'failed';
        state.users.error = action.error.message || 'Failed to load user reports';
      })
      .addCase(updateUserReportStatus.fulfilled, (state: Draft<ReportsState>, action) => {
        const index = state.users.data.findIndex((report) => report.id === action.payload.id);
        if (index !== -1) {
          state.users.data[index] = action.payload;
        }
        // Also update detail if it's the same report
        if (state.userDetail.data?.id === action.payload.id) {
          state.userDetail.data = action.payload;
        }
      })
      // User Report Detail
      .addCase(loadUserReportDetail.pending, (state: Draft<ReportsState>) => {
        state.userDetail.status = 'loading';
        state.userDetail.error = '';
      })
      .addCase(
        loadUserReportDetail.fulfilled,
        (state: Draft<ReportsState>, action: PayloadAction<IAppUserReport>) => {
          state.userDetail.status = 'succeeded';
          state.userDetail.data = action.payload;
          state.userDetail.error = '';
        }
      )
      .addCase(loadUserReportDetail.rejected, (state: Draft<ReportsState>, action) => {
        state.userDetail.status = 'failed';
        state.userDetail.error = action.error.message || 'Failed to load user report';
      });
  },
});

export const reportsReducer = slice.reducer;

// ----------------------------------------------------------------------
// Selectors
// ----------------------------------------------------------------------

export const selectTrainingReports = (state: RootState) => state.reports.trainings;
export const selectTrainingReportDetail = (state: RootState) => state.reports.trainingDetail;
export const selectPlaceReports = (state: RootState) => state.reports.places;
export const selectUserReports = (state: RootState) => state.reports.users;
export const selectUserReportDetail = (state: RootState) => state.reports.userDetail;
